import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import {
  Box,
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  CriarEntradaDeNotasFormData,
  criarEntradaDeNotasSchema,
} from "./schema/criar-entrada-de-notas.schema";
import { ServiceInvoiceStatusEnum } from "../../shared/utils/enum/enum";
import { entradaDeNotasService } from "./service/entrada-de-notas.service";
import { SelectCliente } from "../../shared/components/formulario/input-select-cliente/selectCliente";
import { SelectObra } from "../../shared/components/formulario/input-select-obra/selectObra";
import InputData from "../../shared/components/formulario/input-data/inputData";
import InputMascarado from "../../shared/components/formulario/input-mascarado/InputMascarado";
import { mascaraMoedaReal } from "../../shared/utils/mascaras/mascaraMoedaReal";
import { mascaraCnpj } from "../../shared/utils/mascaras";
import { useMutation } from "@tanstack/react-query";
import { toastError } from "../../shared/services/errors/erros-handler";
import { useEffect } from "react";
import { formatDate } from "../../shared/utils";
import InputDescricao from "../../shared/components/formulario/input-descricao/inputDescricao";
import Input from "../../shared/components/formulario/input/input";

export function EditarNota() {
  const navigate = useNavigate();

  const methods = useForm({
    resolver: yupResolver(criarEntradaDeNotasSchema),
    defaultValues: {
      number: "",
      idWork: "",
      idClient: "",
      providerPhone: "",
      providerEmail: "",
      customerPhone: "",
      customerEmail: "",
      status: ServiceInvoiceStatusEnum.GENERATED,
    },
  });

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = methods;

  const idClient = watch("idClient");
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    if (id) {
      entradaDeNotasService
        .detalhar(id)
        .then((nota) => {
          methods.reset({
            ...nota,
            idCompany: nota.idCompany,
            issueDate: formatDate(nota.issueDate),
            competenceDate: formatDate(nota.competenceDate),
            serviceValue: Number(nota.serviceValue),
            netValue: Number(nota.netValue),
          });
        })
        .catch((error) => {
          toastError(error);
        });
    }
    // eslint-disable-next-line
  }, [id, methods]);

  const { isPending, mutate } = useMutation({
    mutationFn: async (data: CriarEntradaDeNotasFormData) => {
      return await entradaDeNotasService.editar(id || "", data);
    },
    onSuccess: () => {
      toast.success("Nota fiscal editada com sucesso!");
      navigate("/financeiro/entrada-notas-servicos");
    },
    onError: (err: any) => {
      toastError(err);
    },
  });

  return (
    <BaseLayout title="Editar Nota Fiscal">
      <FerramanetasDetalhe
        mostraBotaoApagar={false}
        mostraBotaoNovo={false}
        mostraBotaoSalvarEVoltar={false}
        mostraBotaoVoltar
        aoClicarBotaoSalvar={handleSubmit((formData) => mutate(formData))}
        aoClicarBotaoVoltar={() =>
          navigate("/financeiro/entrada-notas-servicos")
        }
      />
      <Box
        component="form"
        onSubmit={handleSubmit((formData) => mutate(formData))}
      >
        {/* Cliente */}
        <Grid container spacing={2} paddingBottom={2}>
          <Grid item xs={12} md={6}>
            <Controller
              name="idClient"
              control={control}
              render={({ field }) => (
                <SelectCliente
                  value={field.value || ""}
                  onChange={(id, name) => {
                    field.onChange(id);
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="idWork"
              control={control}
              render={({ field }) => (
                <SelectObra
                  value={field.value || ""}
                  onChange={(id, name) => {
                    field.onChange(id);
                  }}
                  idCliente={idClient || ""}
                />
              )}
            />
          </Grid>
        </Grid>
        {/* 📄 Dados da Nota */}
        <Typography variant="h6" gutterBottom>
          📄 Dados da Nota
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Controller
              name="number"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Número da NFS-e"
                  error={!!errors.number}
                  helperText={errors.number?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Controller
              name="dps"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Número DPS"
                  error={!!errors.dps}
                  helperText={errors.dps?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Controller
              name="series"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Número da Série"
                  error={!!errors.series}
                  helperText={errors.series?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <InputData name="issueDate" methods={methods} label="Emitida em" />
          </Grid>
          <Grid item xs={12} md={3}>
            <InputData
              name="competenceDate"
              methods={methods}
              label="Competência"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Controller
              name="verificationCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Código de Verificação"
                  error={!!errors.verificationCode}
                  helperText={errors.verificationCode?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Controller
              name="incidenceMunicipalityCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Código Município Incidência ISSQN"
                  error={!!errors.incidenceMunicipalityCode}
                  helperText={errors.incidenceMunicipalityCode?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* 🏢 Prestador de Serviços */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          🏢 Prestador de Serviços
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Controller
              name="providerName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Razão Social"
                  error={!!errors.providerName}
                  helperText={errors.providerName?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <InputMascarado
              name="providerCnpj"
              methods={methods}
              label="CNPJ"
              mask={mascaraCnpj}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="providerMunicipalRegistration"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Inscrição Municipal"
                  error={!!errors.providerMunicipalRegistration}
                  helperText={errors.providerMunicipalRegistration?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Controller
              name="providerAddress"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Endereço"
                  error={!!errors.providerAddress}
                  helperText={errors.providerAddress?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="providerPhone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Telefone"
                  error={!!errors.providerPhone}
                  helperText={errors.providerPhone?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* 👤 Tomador de Serviços */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          👤 Tomador de Serviços
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Controller
              name="customerName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Razão Social"
                  error={!!errors.customerName}
                  helperText={errors.customerName?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <InputMascarado
              name="customerCnpj"
              methods={methods}
              label="CNPJ"
              mask={mascaraCnpj}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="customerMunicipalRegistration"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Inscrição Municipal"
                  error={!!errors.customerMunicipalRegistration}
                  helperText={errors.customerMunicipalRegistration?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Controller
              name="customerAddress"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Endereço"
                  error={!!errors.customerAddress}
                  helperText={errors.customerAddress?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="customerPhone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Telefone"
                  error={!!errors.customerPhone}
                  helperText={errors.customerPhone?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* 🛠️ Discriminação dos Serviços */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          🛠️ Discriminação dos Serviços
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputDescricao
              name="serviceDescription"
              methods={methods}
              label="Descrição"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="municipalTaxCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Código Tributação Município (CTISS)"
                  error={!!errors.municipalTaxCode}
                  helperText={errors.municipalTaxCode?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="serviceListSubitem"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Subitem Lista de Serviços LC 116/03"
                  error={!!errors.serviceListSubitem}
                  helperText={errors.serviceListSubitem?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="serviceListDescription"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Descrição CTISS"
                  error={!!errors.serviceListDescription}
                  helperText={errors.serviceListDescription?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="operationNature"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Natureza da Operação"
                  error={!!errors.operationNature}
                  helperText={errors.operationNature?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="specialTaxRegime"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Regime Especial de Tributação"
                  error={!!errors.specialTaxRegime}
                  helperText={errors.specialTaxRegime?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* 💰 Valores */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          💰 Valores
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <InputMascarado
              name="serviceValue"
              methods={methods}
              label="Valor dos Serviços"
              mask={mascaraMoedaReal}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Controller
              name="discounts"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Descontos"
                  type="number"
                  error={!!errors.discounts}
                  helperText={errors.discounts?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Controller
              name="federalRetentions"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Retenções Federais"
                  type="number"
                  error={!!errors.federalRetentions}
                  helperText={errors.federalRetentions?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Controller
              name="issWithheld"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="ISS Retido na Fonte"
                  type="number"
                  error={!!errors.issWithheld}
                  helperText={errors.issWithheld?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <InputMascarado
              name="netValue"
              methods={methods}
              label="Valor Líquido"
              mask={mascaraMoedaReal}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Controller
              name="deductions"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Deduções"
                  type="number"
                  error={!!errors.deductions}
                  helperText={errors.deductions?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Controller
              name="unconditionalDiscount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Desconto Incondicionado"
                  type="number"
                  error={!!errors.unconditionalDiscount}
                  helperText={errors.unconditionalDiscount?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Controller
              name="calculationBase"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Base de Cálculo"
                  type="number"
                  error={!!errors.calculationBase}
                  helperText={errors.calculationBase?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Controller
              name="aliquot"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Alíquota"
                  error={!!errors.aliquot}
                  helperText={errors.aliquot?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Controller
              name="issValue"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Valor do ISS"
                  type="number"
                  error={!!errors.issValue}
                  helperText={errors.issValue?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* 🔑 Chave de Acesso */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          🔑 Chave de Acesso
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Controller
              name="accessKey"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Chave de Acesso"
                  error={!!errors.accessKey}
                  helperText={errors.accessKey?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* 🏛️ Observações */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          🏛️ Observações
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputDescricao
              name="otherInfo"
              methods={methods}
              label="Outras Informações"
            />
          </Grid>
        </Grid>

        {/* Status */}
        <Grid container spacing={2} sx={{ mt: 3 }}>
          <Grid item xs={12} md={4}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Status"
                  error={!!errors.status}
                  helperText={errors.status?.message}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value={ServiceInvoiceStatusEnum.GENERATED}>
                    Gerada
                  </MenuItem>
                  <MenuItem value={ServiceInvoiceStatusEnum.CANCELED}>
                    Cancelada
                  </MenuItem>
                </TextField>
              )}
            />
          </Grid>
        </Grid>
      </Box>
    </BaseLayout>
  );
}
